import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { RadioGroup, Form, FormSpacer } from 'components';
import { FormStepContentProps } from 'types';
import { ConfirmDetailsFormFields, FormDataType, useFormState } from 'contexts/FormProvider';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { useContentContext } from '@qbe/dfc-content';
import validationSchema from './schema';
import { Button } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { yupResolver } from '@hookform/resolvers/yup';
import { setTransactionStartEvent } from 'hooks/useAnalytics';
import { useContentComplaintPartners } from 'utils/content';

const VerifyEligibilityForm = ({ defaultValues, onComplete, onStart }: FormStepContentProps) => {
  useCheckWindowCMS();

  // Form logic hook
  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<ConfirmDetailsFormFields>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(validationSchema) as any,
  });

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const isValid = validationSchema.isValidSync(watch());

  const { formData } = useFormState();

  // Container for info box
  const formRef = useRef(null);

  const onSubmit = (newData: Partial<FormDataType>) => {
    onComplete({
      ...newData,

      // Reset files if new lodgment
      files: formData.lodgement !== newData.lodgement ? [] : newData.files,
    });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} ref={formRef} data-test="provider-form">
      <FormCard
        variant="pageTitle"
        title={getTextContent('provider.form.heading')}
        description={getTextContent('provider.form.contentParagraph')}
      />

      <FormCard variant="content">
        <Controller
          name="lodgement"
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <RadioGroup
              {...field}
              onChange={(value) => {
                field.onChange(value);
                setTransactionStartEvent();
              }}
              styleVariant="skinny"
              direction="vertical"
              defaultValue=""
              title={getTextContent('provider.form.lodgementType')}
              radioList={[
                {
                  id: 'Complaint',
                  label: getTextContent('provider.form.lodgement.0.label'),
                  value: 'complaint',
                },
                {
                  id: 'Financial-hardship-application',
                  label: getTextContent('provider.form.lodgement.1.label'),
                  value: 'fha',
                },
                {
                  id: 'Incident/Breach',
                  label: getTextContent('provider.form.lodgement.2.label'),
                  value: 'breach',
                },
              ]}
              data-test="BreachOrComplaintOrFha"
              data-track="BreachOrComplaintOrFha"
              data-track-value="capture"
            />
          )}
        />
        <FormSpacer type="formgroup" />
        <Button
          {...{
            label: getTextContent('provider.form.submit.label'),
            type: 'submit',
            disabled: !isValid,
          }}
        />
      </FormCard>
    </Form>
  );
};

export default VerifyEligibilityForm;
